/**
 * PHÂN GIẢI ĐƯỜNG DẪN ẢNH AI — MỘT CHỖ DUY NHẤT biết ảnh nằm ở `.webp` hay `.png`.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * VÌ SAO CẦN MODULE NÀY
 * ────────────────────────────────────────────────────────────────────────────
 * `tools/sync-art.mjs` nén ảnh gốc `art/ai/*.png` thành `public/art/ai/*.webp`
 * (giảm ~93%). Nhưng đường dẫn ảnh bị HARDCODE ở RẤT nhiều nơi:
 *   · `AI_SHEETS` / `AI_ENEMY_SHEETS` trong `src/art/ai-parts.ts`
 *   · `ARENA_ART` trong `src/render/battlefield-art.ts`
 *   · `VFX_TEX` trong `src/render/vfx-textures.ts` + `ultFormUrl()`
 *   · `classSheetUrl()` trong `src/ui/hud.ts`
 *   · đường dẫn ĐỘNG `` `art/ai/${artId}-parts.png` `` (vfx + art ultimate)
 *
 * Sửa từng chỗ là dễ sót — mà sót 1 chỗ thì ảnh đó **404 và im lặng** (game lùi về
 * art vector, người dùng chỉ thấy "tự nhiên hình khác đi", rất khó đoán nguyên nhân).
 * Vì vậy mọi nơi đi qua ĐÚNG 2 hàm dưới đây.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * VÌ SAO DÙNG `suffix` MÀ KHÔNG PHẢI `.webp` CỐ ĐỊNH
 * ────────────────────────────────────────────────────────────────────────────
 * `sync-art.mjs` có chế độ LÙI: chưa cài `sharp` thì nó COPY NGUYÊN PNG. Lúc đó game
 * PHẢI nạp `.png`, nếu cứng nhắc `.webp` là ảnh 404 hết.
 * `sync-art.mjs` ghi đuôi thật đang dùng vào `public/art/ai/.ext`, trang nạp đọc
 * file đó TRƯỚC khi dựng art. Không đọc được (file thiếu/ lỗi) ⇒ lùi về `.webp`
 * (trường hợp bình thường, vì `sharp` có trong devDependencies).
 *
 * ⚠️ ĐỔI CÁCH LÀM Ở ĐÂY THÌ PHẢI SỬA CẢ `tools/sync-art.mjs` — hai bên là một CẶP.
 */

/** Đuôi file đang dùng cho ảnh AI — mặc định `.webp`. */
let ext = 'webp';

/** Trạng thái nạp: `undefined` = chưa thử, `true` = đã có kết quả (kể cả lùi về mặc định). */
let loaded: Promise<void> | undefined;

/**
 * ĐỌC ĐUÔI THẬT mà `sync-art.mjs` đã ghi. Gọi 1 lần lúc boot, TRƯỚC khi dựng art.
 *
 * Không bao giờ ném lỗi: thiếu file là chuyện bình thường khi chạy `vite dev` mà
 * chưa `sync:art` — lúc đó lùi về `.webp` và ảnh có thể 404, nhưng game KHÔNG vỡ
 * (mọi chỗ nạp ảnh đều đã bọc `safe()`/ trả `null`).
 */
export async function loadArtExt(): Promise<void> {
  if (loaded) return loaded;
  loaded = (async () => {
    try {
      const r = await fetch('art/ai/.ext', { cache: 'no-cache' });
      if (!r.ok) return;
      const t = (await r.text()).trim().toLowerCase();
      if (t === 'png' || t === 'webp') ext = t;
    } catch {
      /* im lặng: giữ mặc định `.webp` */
    }
  })();
  return loaded;
}

/** Đuôi đang dùng (không có dấu chấm) — cho công cụ kiểm chứng đọc lại. */
export function artExt(): string {
  return ext;
}

/**
 * ĐỔI ĐƯỜNG DẪN `.png` CỦA ẢNH AI SANG ĐUÔI ĐANG DÙNG.
 *
 * Dùng cho các hằng số đã hardcode sẵn (VD `'art/ai/arena-bg.png'`). Chỉ đổi phần
 * ĐUÔI CUỐI, giữ nguyên đường dẫn — nên gọi được với mọi hằng số hiện có mà không
 * phải sửa giá trị của chúng.
 */
export function artUrl(path: string): string {
  return path.replace(/\.png$/i, `.${ext}`);
}

/**
 * DỰNG đường dẫn cho ảnh đặt tên theo quy ước `<artId>-parts.png`.
 * Dùng cho đường dẫn ĐỘNG (art ultimate, vfx) — nơi hằng số không liệt kê được.
 */
export function artPartsUrl(artId: string): string {
  return `art/ai/${artId}-parts.${ext}`;
}
